import { z } from 'zod';
/**
 * Pedidos de medicamento da clínica (estudo da mesa, decisões 2 e 3). O ciclo é o do
 * APOLO (`medication_orders`): pedido a fazer, pedido feito, a caminho, chegou na
 * clínica, entrega agendada, entregue; atraso depois de 15 dias. Liga por clínica
 * (Configurações); o tratamento do catálogo que tem medicamentos abre o pedido sozinho.
 */
const isoDateTime = z.iso.datetime();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const CLINIC_MEDICATION_STEPS = [
    'a_pedir',
    'pedido',
    'a_caminho',
    'chegou',
    'agendado',
    'entregue',
];
export const CLINIC_MEDICATION_STATUSES = [...CLINIC_MEDICATION_STEPS, 'cancelado'];
export const clinicMedicationStatusSchema = z.enum(CLINIC_MEDICATION_STATUSES);
export const CLINIC_MEDICATION_STATUS_LABEL = {
    a_pedir: 'Pedido a fazer',
    pedido: 'Pedido feito',
    a_caminho: 'A caminho',
    chegou: 'Chegou: agendar entrega',
    agendado: 'Entrega agendada',
    entregue: 'Entregue',
    cancelado: 'Cancelado',
};
/** O verbo do botão que leva ao passo seguinte. */
export const CLINIC_MEDICATION_NEXT_LABEL = {
    a_pedir: 'Fiz o pedido',
    pedido: 'Saiu do fornecedor',
    a_caminho: 'Chegou na clínica',
    chegou: 'Agendar a entrega',
    agendado: 'Entreguei',
};
/** Dias desde o pedido feito para o pedido contar como atrasado (regra do APOLO). */
export const CLINIC_MEDICATION_LATE_DAYS = 15;
export const CLINIC_MEDICATION_ITEMS_MAX = 10;
/** Unidades de quem dá entrada no que chegou (as do APOLO, com a caneta). */
export const CLINIC_MEDICATION_UNITS = [
    'caixa',
    'frasco',
    'caneta',
    'comprimido',
    'ampola',
    'blister',
    'envelope',
    'outro',
];
export const clinicMedicationItemSchema = z.object({
    name: z.string().trim().min(1).max(80),
    /** Como a clínica escreve: "2 frascos", "1 caixa". */
    quantity: z.string().trim().max(40),
    /** Do cadastro de medicamentos (custo e laboratório vêm de lá). */
    medicationId: z.uuid().nullable().optional(),
    /** Custo deste item, para o financeiro. */
    costCents: z.number().int().min(0).max(10_000_000).nullable().optional(),
    // Cada item tem o seu fornecedor, pedido, rastreio e datas (como no APOLO).
    supplier: z.string().trim().max(80).nullable().optional(),
    supplierOrder: z.string().trim().max(80).nullable().optional(),
    tracking: z.string().trim().max(80).nullable().optional(),
    /** Previsão de entrega (pedido + 15 dias, editável). */
    plannedDay: isoDate.nullable().optional(),
    shippedDay: isoDate.nullable().optional(),
    receivedDay: isoDate.nullable().optional(),
    receivedQty: z.string().trim().max(40).nullable().optional(),
    receivedUnit: z.enum(CLINIC_MEDICATION_UNITS).nullable().optional(),
});
export const clinicMedicationEventSchema = z.object({
    text: z.string(),
    actor: z.string(),
    at: isoDateTime,
});
export const clinicMedicationOrderSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    patientName: z.string(),
    phone: z.string().nullable(),
    treatmentId: z.uuid().nullable(),
    treatmentName: z.string().nullable(),
    items: z.array(clinicMedicationItemSchema),
    supplier: z.string().nullable(),
    supplierOrder: z.string().nullable(),
    tracking: z.string().nullable(),
    status: clinicMedicationStatusSchema,
    notes: z.string(),
    /** Quando o pedido foi feito ao fornecedor (conta o atraso a partir daqui). */
    orderedAt: isoDateTime.nullable(),
    shippedAt: isoDateTime.nullable(),
    arrivedAt: isoDateTime.nullable(),
    /** Entrega marcada na agenda (consulta). */
    deliveryAppointmentId: z.uuid().nullable(),
    deliveryAt: isoDateTime.nullable(),
    deliveredAt: isoDateTime.nullable(),
    /** Dias desde o pedido feito (ou desde a criação, se ainda não foi pedido). */
    days: z.number().int().min(0),
    late: z.boolean(),
    history: z.array(clinicMedicationEventSchema),
    createdAt: isoDateTime,
    /** Padrão (do tratamento), extra ou troca (com o motivo). */
    kind: z.enum(['padrao', 'extra', 'troca']).optional(),
    swapReason: z.string().nullable().optional(),
    /** Soma do custo dos itens. */
    costCents: z.number().int().min(0).nullable().optional(),
    /** Na entrega: parcela vencida do paciente (avisa, não bloqueia). */
    billingAlert: z
        .object({ label: z.string(), cents: z.number().int(), days: z.number().int().min(0) })
        .nullable()
        .optional(),
});
/** Cadastro de medicamentos da clínica (o "Catálogo" do APOLO), com o custo. */
export const clinicMedicationSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    presentation: z.string(),
    laboratory: z.string(),
    unitCostCents: z.number().int().min(0).nullable(),
    active: z.boolean(),
});
export const saveClinicMedicationSchema = z.object({
    name: z.string().trim().min(2).max(80),
    presentation: z.string().trim().max(80).optional(),
    laboratory: z.string().trim().max(80).optional(),
    unitCostCents: z.number().int().min(0).max(10_000_000).nullable().optional(),
    active: z.boolean().optional(),
});
export const createClinicMedicationOrderSchema = z.object({
    patientId: z.uuid(),
    treatmentId: z.uuid().optional(),
    items: z.array(clinicMedicationItemSchema).min(1).max(CLINIC_MEDICATION_ITEMS_MAX),
    supplier: z.string().trim().max(80).optional(),
    notes: z.string().trim().max(500).optional(),
    kind: z.enum(['padrao', 'extra', 'troca']).optional(),
    /** Obrigatório na troca (no APOLO o campo existe e nunca é gravado). */
    swapReason: z.string().trim().max(200).optional(),
});
export const updateClinicMedicationOrderSchema = z
    .object({
    items: z.array(clinicMedicationItemSchema).min(1).max(CLINIC_MEDICATION_ITEMS_MAX),
    supplier: z.string().trim().max(80).nullable(),
    supplierOrder: z.string().trim().max(80).nullable(),
    tracking: z.string().trim().max(80).nullable(),
    notes: z.string().trim().max(500),
})
    .partial();
/** Andar o pedido: o passo seguinte (ou cancelar). A entrega agendada aponta a consulta. */
export const advanceClinicMedicationOrderSchema = z.object({
    to: clinicMedicationStatusSchema,
    appointmentId: z.uuid().optional(),
    note: z.string().trim().max(200).optional(),
    /** "Chegou": a entrada de cada item (data, quantidade e unidade recebidas). */
    received: z
        .array(z.object({
        index: z.number().int().min(0).max(20),
        day: isoDate,
        qty: z.string().trim().max(40).optional(),
        unit: z.enum(CLINIC_MEDICATION_UNITS).optional(),
    }))
        .max(20)
        .optional(),
});
